﻿import PackageContext from '@/contexts/PackageContext';
import usePostalAddressSync from '@/hooks/usePostalAddressSync';
import useSponsors from '@/hooks/useSponsors';
import { countryIsAustralia, requiresSpecialitySpecification, specifyDietaryRequirementCondition } from '@/utils/conditions';
import { convert, parse } from '@/utils/converters';
import getMembershipTypeText from '@/utils/getMembershipTypeText';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Grid, InputAdornment, styled, TextField, Typography } from '@mui/material';
import { Checkbox, DateTimePicker, InputOnlyField, Numeric, Select, Text, ToggleButtonGroup } from '@ngt/forms';
import { Condition, ConditionMode, useFormActions, useScopedField } from '@ngt/forms-core';
import { useContext, useEffect, useState } from 'react';
import * as Dtos from '../../api/dtos';
import AttendanceCheck from '../AttendanceCheck';
import CapCheck from '../CapCheck';
import { ErrorFields } from '../errors/ErrorFields';
import MemberCheck from '../MemberCheck';
import Packages from '../registration/packages/Packages';
import RegistrationField from '../RegistrationField';
import SacCheck from '../SacCheck';
import StaffCheck from '../StaffCheck';
import AdminPaymentDisplay from './AdminPaymentDisplay';
import AdminPaymentForm from './AdminPaymentForm';
import AdminPaymentTable from './AdminPaymentTable';

const columnMapping = {
    xs: 12,
    sm: 6,
    md: 4
}

const MemberStaffCheckGrid = styled(Grid)(({theme}) => ({
    width: '100%',
    textAlign: 'left',
    display: 'block',
    paddingTop: 10,
    paddingBottom: 10,

    [theme.breakpoints.up('lg')]: {
        display: 'flex',
        gap: theme.spacing(3),
    },

}))

const GridMember = styled(Grid)(() => ({
    flex: '1 1 25%'

}))

const GridStaff = styled(Grid)(() => ({
    flex: '1 1 25%'

}))

const StyledAccordionSummary = styled(AccordionSummary)(({theme}) => ({
    backgroundColor: theme.palette.primary.main,
    borderRadius: "4px",

}))

const GridSac = styled(Grid)(() => ({
    flex: '1 1 25%'

}))

const GridCap = styled(Grid)(() => ({
    flex: '1 1 25%'

}))

const usePackageId = () => {
    const {state: { value: packageId }, context: { setValue: setPackageId }} = useScopedField<number>("packageId", {value: true});

    return {packageId, setPackageId};
}

const valueSubscription = {
    value: true
};

const roomCompanionSelectedCondition = ({value}: any) => value.roomCompanion == 1;

const customRegistrationAvailableCondition = ({value}: any) => value.freeRegistration == null || value.freeRegistration == false;

const customRegistrationSelectedCondition = ({value}: any) => value.customRegistration === true;

const pricingNotesEnabledCondition = ({value}: any) => value?.customRegistration === true || value?.freeRegistration === true || value?.sponsor !== undefined;

const sponsorSelectedCondition = ({value}: any) => value?.sponsor === true;

const supervisorNameEnabledCondition = ({value}: any) => value?.trialCoordinator === true || value?.student === true || value?.registrar === true;

const otherSelectedCondition = ({value}: any) => value.other === true;

const bctFlightsArrangedCondition = ({value}: any) => value.bctFlights == 1;

const bctTransfersArrangedCondition = ({value}: any) => value.bctTransfers == 1;

const AdminRegistrationSteps = [
    {
        //#region Step 1
        label: '',
        Inputs: () => {
            
        const {state: {value: membership}} = useScopedField<Dtos.MembershipType>("membership", {value: true});

        return (
            <>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Grid
                        container
                        spacing={4}
                    >
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Title"
                                propertyPath="title"
                                component={Select}
                                FormControlProps={{
                                    variant: "standard"
                                }}
                                sx={{marginTop:"16px"}}
                            />
                        </Grid>
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="First Name"
                                propertyPath="firstName"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Last Name"
                                propertyPath="lastName"
                                component={Text}
                                variant="standard"
                                margin="normal"
                            />
                        </Grid>
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <TextField
                                label="Membership Type"
                                variant="standard"
                                sx={{
                                    width: '100%',
                                    mt: 2,
                                    mb: 1,
                                }}
                                InputProps={{
                                    readOnly: true,
                                }}
                                value={getMembershipTypeText(membership ?? undefined)}
                            />
                        </Grid>
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Speciality"
                                propertyPath="speciality"
                                component={Select}
                                FormControlProps={{
                                    variant: "standard"
                                }}
                                sx={{marginTop:"16px",
                                marginBottom:"8px"}}                        
                            />
                        </Grid>
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <Condition 
                                condition={requiresSpecialitySpecification}
                                subscription={valueSubscription}
                                mode={ConditionMode.Enable}
                            >
                                <InputOnlyField
                                    label="Specify Other"
                                    propertyPath="specialitySpec"
                                    component={Text}
                                    variant="standard"
                                    margin="normal"
                                />
                            </Condition>
                        </Grid>
                    </Grid>
                </Box>
                <ErrorFields
                    errorFields={[
                        "title",
                        "firstName",
                        "lastName",
                        "speciality",
                        "specialitySpec"
                    ]}
                />
                <br />
            </>
        )
        }
        //#endregion
    },
    {
        //#region Addresses
        label: 'Addresses',
        Inputs: () => {
       
        const {sameAsAddress, sameStateAsAddress} = usePostalAddressSync();
        
        return (
            <>
            <Accordion elevation={4}>
                <StyledAccordionSummary 
                    expandIcon={<FontAwesomeIcon fixedWidth icon={faChevronDown} color='white' />}
                >
                    <Typography variant="h3" color="white"><b>Addresses</b></Typography>   
                </StyledAccordionSummary>
                <AccordionDetails>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Grid
                            container
                            spacing={4}
                        >
                            <Grid
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Institution"
                                    propertyPath="institution"
                                    component={Text}
                                    variant="standard"
                                    margin="normal"
                                />
                            </Grid>
                            <Grid
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Address"
                                    propertyPath="address"
                                    component={Text}
                                    variant="standard"
                                    margin="normal"
                                />
                            </Grid>
                            <Grid
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Suburb"
                                    propertyPath="suburb"
                                    component={Text}
                                    variant="standard"
                                    margin="normal"
                                />
                            </Grid>
                            <Grid
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Postcode"
                                    propertyPath="postcode"
                                    component={Text}
                                    variant="standard"
                                    margin="normal"
                                />
                            </Grid>
                            <Grid
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Country"
                                    propertyPath="country"
                                    component={Text}
                                    variant="standard"
                                    margin="normal"
                                />
                            </Grid>
                            <Grid
                                item
                                {...columnMapping}
                            >
                                <Condition 
                                    condition={countryIsAustralia}
                                    subscription={valueSubscription}
                                    mode={ConditionMode.Enable}
                                >
                                    <InputOnlyField
                                        label="State"
                                        propertyPath="state"
                                        component={Select}
                                        FormControlProps={{
                                            variant: "standard"
                                        }}
                                        sx={{marginTop:"16px",
                                        marginBottom:"8px"}}
                                    />
                                </Condition>
                            </Grid>
                            <Grid
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Mobile Phone Number"
                                    propertyPath="mobile"
                                    component={Text}
                                    variant="standard"
                                    margin="normal"
                                />
                            </Grid>
                            <Grid
                                item
                                {...columnMapping}
                            >
                                <InputOnlyField
                                    label="Email"
                                    propertyPath="email"
                                    component={Text}
                                    variant="standard"
                                    margin="normal"
                                />
                            </Grid>
                        
                        <Grid item xs={12}>
                            <Typography
                                variant="h6"
                                sx={{
                                    mt: 5,
                                }}
                                color='secondary'
                            >
                                Postal Details
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <InputOnlyField
                                label="Address Same as Above"
                                propertyPath="addressSame"
                                component={Checkbox}
                            />
                        </Grid>

                        <Grid
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Postal Street"
                                propertyPath="postalStreet"
                                component={Text}
                                variant="standard"
                                margin="normal"
                                disabled={sameAsAddress}
                            />
                        </Grid>
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Postal Suburb"
                                propertyPath="postalSuburb"
                                component={Text}
                                variant="standard"
                                margin="normal"
                                disabled={sameAsAddress}
                            />
                        </Grid>
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Postal State"
                                propertyPath="postalState"
                                component={Text}
                                variant="standard"
                                margin="normal"
                                disabled={sameStateAsAddress}
                            />
                        </Grid>
                        <Grid
                            item
                            {...columnMapping}
                        >
                            <InputOnlyField
                                label="Postal Postcode"
                                propertyPath="postalPostcode"
                                component={Text}
                                variant="standard"
                                margin="normal"
                                disabled={sameAsAddress}
                            />
                        </Grid>
                    </Grid>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <br/>

            <ErrorFields
                errorFields={[
                    "institution",
                    "address",
                    "suburb",
                    "postcode",
                    "country",
                    "state",
                    "email",
                    "postalStreet",
                    "postalSuburb",
                    "postalState",
                    "postalPostcode",
                    "mobile",
                    "originCity"
                ]}
            />
            
            </>
            
        )}
        
        //#endregion
    },
    {
        //#region Delegate Options
        label: 'Delegate Options',
        Inputs: () => {
        
        return (
            <>
                <Accordion elevation={4}>
                    <StyledAccordionSummary 
                        expandIcon={<FontAwesomeIcon fixedWidth icon={faChevronDown} color='white' />}
                    >
                        <Typography variant="h3" color="white"><b>Delegate Options</b></Typography>   
                    </StyledAccordionSummary>
                    <AccordionDetails>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Grid
                                container
                                spacing={4}
                            >
                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 12,
                                        md: 12
                                    }}
                                >
                                    <InputOnlyField
                                        label="Early/Middle Career in research"
                                        propertyPath="earlyMidCareer"
                                        component={Checkbox}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 8,
                                        md: 3
                                    }}
                                >
                                    <Typography>Allow contact details to be included in delegate listing</Typography>
                                </Grid>
                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 8,
                                        md: 3
                                    }}
                                >
                                    <RegistrationField
                                        label="Allow contact details to be included in delegate listing"
                                        propertyPath="includeDetails"
                                        component={ToggleButtonGroup}
                                        sx={{margin:"normal"}}
                                        parse={parse}
                                        convert={convert}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 8,
                                        md: 3
                                    }}
                                >
                                    <Typography>Receive certificate of attendance</Typography>
                                </Grid>
                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 8,
                                        md: 3
                                    }}
                                >
                                    <RegistrationField
                                        label="Receive certificate of attendance"
                                        propertyPath="receiveCertificate"
                                        component={ToggleButtonGroup}
                                        sx={{margin:"normal"}}
                                        parse={parse}
                                        convert={convert}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 8,
                                        md: 3
                                    }}
                                >
                                    <Typography>Requires disability access</Typography>
                                </Grid>
                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 4,
                                        md: 3
                                    }}
                                >
                                    <InputOnlyField
                                        label="Requires disability access"
                                        propertyPath="requiresDisabilityAccess"
                                        component={ToggleButtonGroup}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 8,
                                        md: 3
                                    }}
                                >
                                    <Typography>Requires audio/visual assistance</Typography>
                                </Grid>
                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 4,
                                        md: 3
                                    }}
                                >
                                    <InputOnlyField
                                        label="Requires audio/visual assistance"
                                        propertyPath="requiresAVAssistance"
                                        component={ToggleButtonGroup}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    {...{xs: 12,
                                        sm: 12,
                                        md: 6}}
                                    marginTop='20px'
                                >
                                    <InputOnlyField
                                        propertyPath="dietaryRequirement"
                                        label="Dietary Requirement"
                                        component={Select}
                                        FormControlProps={{
                                            variant: "standard"
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    {...{xs: 12,
                                        sm: 12,
                                        md: 6}}
                                    marginTop='20px'
                                >
                                    <Condition 
                                        condition={specifyDietaryRequirementCondition}
                                        subscription={valueSubscription}
                                        mode={ConditionMode.Enable}
                                    >
                                        <InputOnlyField
                                            propertyPath="dietaryRequirements"
                                            label="Dietary Requirements"
                                            component={Text}
                                            variant="standard"
                                        />
                                        </Condition>
                                </Grid>
                                
                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 8,
                                        md: 3
                                    }}
                                >
                                    <Typography>Room companion</Typography>
                                </Grid>

                                <Grid
                                    item
                                    {...{
                                        xs: 12,
                                        sm: 4,
                                        md: 3
                                    }}
                                >
                                    <InputOnlyField
                                        label="Room Companion"
                                        propertyPath="roomCompanion"
                                        component={ToggleButtonGroup}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>

                                <Condition
                                    condition={roomCompanionSelectedCondition}
                                    subscription={valueSubscription}
                                    mode={ConditionMode.Show}
                                >
                                    <Grid
                                        item
                                        {...{xs: 12,
                                            sm: 12,
                                            md: 6}}
                                        marginTop='20px'
                                    >
                                        <InputOnlyField
                                            propertyPath="roomCompanionName"
                                            label="Companion Name"
                                            component={Text}
                                            variant="standard"
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        {...{xs: 12,
                                            sm: 12,
                                            md: 6}}
                                        marginTop='20px'
                                    >
                                        <InputOnlyField
                                            propertyPath="roomCompanionDietaryRequirements"
                                            label="Companion Dietary Requirements"
                                            component={Text}
                                            variant="standard"
                                        />
                                    </Grid>
                                </Condition>
                            </Grid>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                <br/>
                
                <ErrorFields
                    errorFields={[
                        "earlyMidCareer",
                        "includeDetails",
                        "receiveCertificate",
                        "requiresDisabilityAccess",
                        "requiresAVAssistance",
                        "dietaryRequirements",
                        "roomCompanion",
                        "roomCompanionName",
                        "roomCompanionDietaryRequirements"
                    ]}
                />
            </>
            )
        }
        //#endregion
    },
    {
        //#region Registration
        label: 'Registration',
        Inputs: () => {
        const {state: {value: formCost}} = useScopedField<number>("cost", {value: true});
        const { sponsors  } = useSponsors();

        const {state: {value: staffConfirmed}} = useScopedField<boolean>("staffConfirmed", {value: true});
        const {state: {value: Sac}} = useScopedField<boolean>("isSac", {value: true});
        const {state: {value: Cap}} = useScopedField<boolean>("isCap", {value: true});
        const {state: {value: member}} = useScopedField<number>("member", {value: true});
        const {state: {value: firstName}} = useScopedField<string>("firstName", {value: true});
        const {state: {value: lastName}} = useScopedField<string>("lastName", {value: true});

        return (
            <>
                <Accordion elevation={4}>
                    <StyledAccordionSummary 
                        expandIcon={<FontAwesomeIcon fixedWidth icon={faChevronDown} color='white'/>}
                        style={{backgroundColor: 'primary' }}
                        color='primary'
                    >
                        <Grid
                            container
                        >
                            <Grid 
                                item
                                xs={12}
                            >
                                <Typography variant="h3" color="white" style={{paddingBottom: "8px"}}><b>Registration</b></Typography>
                            </Grid>
                            <Grid 
                                item
                                xs={12}
                                sm={12}
                                md={6}
                                lg={6}
                                xl={3}
                            >
                                <Typography variant="h6" color='white'><b>Staff Status:</b> {!firstName || !lastName ? "Unknown" : staffConfirmed === true ? "Staff" : "Not Staff"}</Typography>
                            </Grid>
                            <Grid 
                                item
                                xs={12}
                                sm={12}
                                md={6}
                                lg={6}
                                xl={3}
                            >
                                <Typography variant="h6" color='white'><b>SAC Status:</b> {!firstName || !lastName ? "Unknown" : Sac === true ? "Sac" : "Not Sac"}</Typography>
                            </Grid>
                            <Grid 
                                item
                                xs={12}
                                sm={12}
                                md={6}
                                lg={6}
                                xl={3}
                            >
                                <Typography variant="h6" color='white'><b>CAP Status:</b> {!firstName || !lastName ? "Unknown" : Cap === true ? "Cap" : "Not Cap"}</Typography>
                            </Grid>
                            <Grid 
                                item
                                xs={12}
                                sm={12}
                                md={6}
                                lg={6}
                                xl={3}
                            >
                                <Typography variant="h6" color='white'><b>Member Status:</b> {!firstName || !lastName ? "Unknown" : member !== undefined ? "Member" : "Not Member"}</Typography>
                            </Grid>
                        </Grid>
                    </StyledAccordionSummary>
                    <AccordionDetails>
                    <Grid
                        container
                        spacing={2}
                    >
                            <Grid item xs={12}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mt: 5
                                    }}
                                    color='secondary'
                                >
                                    Pricing Options
                                </Typography>
                                <Grid 
                                    container
                                >
                                    <Grid 
                                        item
                                        {...columnMapping}
                                        md={2}
                                        marginTop='20px'
                                    >
                                        <InputOnlyField
                                            label="Free Registration"
                                            propertyPath="freeRegistration"
                                            component={Checkbox}
                                            sx={{margin:'normal'}}
                                            //onChange={(event: any) =>setPrice(event, 0)}
                                        />
                                    </Grid>
                                    <Grid 
                                        item
                                        {...columnMapping}
                                        md={2}
                                        marginTop='20px'
                                    >
                                        <Condition
                                            condition={customRegistrationAvailableCondition}
                                            subscription={valueSubscription}
                                            mode={ConditionMode.Enable}
                                        >
                                            <InputOnlyField
                                                label="Custom Registration"
                                                propertyPath="customRegistration"
                                                component={Checkbox}
                                                sx={{margin:'normal'}}
                                            />
                                        </Condition>
                                    </Grid>
                                    <Grid 
                                        item
                                        {...columnMapping}
                                        md={3}
                                    >
                                        <Condition
                                            condition={customRegistrationSelectedCondition}
                                            subscription={valueSubscription}
                                            mode={ConditionMode.Enable}
                                        >
                                            <InputOnlyField
                                                label="Amount"
                                                propertyPath="customPrice"
                                                variant="standard"
                                                component={Numeric}
                                                margin="normal"
                                            />
                                        </Condition>
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={1}
                                        md={1}
                                        sx={{px: '30px', mt: '30px'}}
                                    >
                                        <Typography
                                            variant="h3"
                                            sx={{display: 'flex',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                            top:0,
                                            zIndex: 2}}
                                        >   
                                            ${formCost}
                                        </Typography>
                                        </Grid>
                                        <Grid item xs={2} md={4} sx={{ justifyContent: 'center', mt: '20px',verticalAlign: 'center'}}>

                                    </Grid>

                                    <MemberStaffCheckGrid>
                                        <GridStaff>
                                            <StaffCheck/>
                                        </GridStaff>
                                        <GridSac>
                                            <SacCheck/>
                                        </GridSac>
                                        <GridCap>
                                            <CapCheck />
                                        </GridCap>
                                        <GridMember>
                                            <MemberCheck />
                                        </GridMember>
                                          <GridMember>
                                            <AttendanceCheck />
                                        </GridMember>

                                    </MemberStaffCheckGrid>

                                    <Grid 
                                        item
                                        xs={12}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Condition
                                            condition={pricingNotesEnabledCondition}
                                            subscription={valueSubscription}
                                            mode={ConditionMode.Enable}
                                        >
                                            <InputOnlyField
                                                label='Notes'
                                                propertyPath="priceNote"
                                                component={Text}
                                                multiline={true}
                                                rows={3}
                                                margin="normal"
                                                
                                            />
                                        </Condition>
                                    </Grid>
                                </Grid>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mt: 5
                                    }}
                                    color='secondary'
                                >
                                    Attendee Type
                                </Typography>
                                <Grid 
                                    container
                                >
                                    <Grid 
                                        item
                                        xs={6}
                                        md={3}
                                    >
                                        <InputOnlyField
                                            label="Standard"
                                            propertyPath="defaultUser"
                                            component={Checkbox}
                                            sx={{margin: 'normal'}}
                                        />
                                    </Grid>

                                    <Grid 
                                        item
                                        xs={6}
                                        md={3}
                                    >
                                        <InputOnlyField
                                            label="Trial Coordinator"
                                            propertyPath="trialCoordinator"
                                            component={Checkbox}
                                            sx={{margin: 'normal'}}
                                        />
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={6}
                                        md={3}
                                    >
                                        <InputOnlyField
                                            label="Student"
                                            propertyPath="student"
                                            component={Checkbox}
                                            sx={{margin: 'normal'}}
                                        />
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={6}
                                        md={3}
                                    >
                                        <InputOnlyField
                                            label="Registrar"
                                            propertyPath="registrar"
                                            component={Checkbox}
                                            sx={{margin: 'normal'}}
                                        />
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={6}
                                        md={3}
                                    >
                                        <InputOnlyField
                                            label="BCT Staff"
                                            propertyPath="staff"
                                            component={Checkbox}
                                            sx={{margin: 'normal'}}
                                        />
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={6}
                                        md={3}
                                    >
                                        <InputOnlyField
                                            label="Attendee from Japan or Korea"
                                            propertyPath="attendeeFromJapanOrKorea"
                                            component={Checkbox}
                                            sx={{margin: 'normal'}}
                                        />
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={6}
                                        md={3}
                                    >
                                        <InputOnlyField
                                            label="Sponsor"
                                            propertyPath="sponsor"
                                            component={Checkbox}
                                            sx={{margin: 'normal'}}
                                        />
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={6}
                                        md={3}
                                    >
                                        <InputOnlyField
                                            label="Speaker"
                                            propertyPath="isSpeaker"
                                            component={Checkbox}
                                            sx={{margin: 'normal'}}
                                        />
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={6}
                                        md={3}
                                    >
                                        <Condition
                                            condition={sponsorSelectedCondition}
                                            subscription={valueSubscription}
                                            mode={ConditionMode.Show}
                                        >
                                            <InputOnlyField
                                                label="Use Free Sponsor Registation"
                                                propertyPath="freeSponsorRegistration"
                                                component={Checkbox}
                                                sx={{margin: 'normal'}}
                                            />
                                        </Condition>
                                        
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={12}
                                        md={6}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Condition
                                            condition={supervisorNameEnabledCondition}
                                            subscription={valueSubscription}
                                            mode={ConditionMode.Enable}
                                        >
                                            <InputOnlyField
                                                label="Name of Supervisor"
                                                propertyPath="supervisorName"
                                                component={Text}
                                                variant="standard"
                                                margin="normal"
                                                sx={{margin: 'normal', width: '90%'}}
                                            />
                                        </Condition>
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={12}
                                        md={6}
                                        sx={{
                                            alignItems: 'bottom'
                                        }}
                                    >
                                        <Condition
                                            condition={sponsorSelectedCondition}
                                            subscription={valueSubscription}
                                            mode={ConditionMode.Enable}
                                        >
                                            <RegistrationField
                                                label="Sponsor Name"
                                                propertyPath="sponsorId"
                                                component={Select}
                                                sx={{
                                                    margin:"normal", 
                                                    width: '90%',
                                                    marginTop:"16px"
                                                }}
                                                FormControlProps={{
                                                    variant: "standard"
                                                }}
                                                options={sponsors?.map((sponsor) => ({value: sponsor.sponsorName, id: sponsor.id}))}
                                            />
                                        </Condition>
                                        
                                    </Grid>
                                    
                                
                                </Grid>
                                <Grid 
                                    container
                                >
                                    <Grid 
                                        item
                                        {...columnMapping}
                                        md={3}
                                        marginTop='25px'

                                    >
                                        <InputOnlyField
                                            label="Other"
                                            propertyPath="other"
                                            component={Checkbox}
                                            sx={{margin: 'normal'}}
                                        />
                                    </Grid>
                                    <Grid 
                                        item
                                        xs={9}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Condition
                                            condition={otherSelectedCondition}
                                            subscription={valueSubscription}
                                            mode={ConditionMode.Enable}
                                        >
                                            <InputOnlyField
                                                label="Please Specify"
                                                propertyPath="otherSpecification"
                                                component={Text}
                                                variant="standard"
                                                margin="normal"
                                            />
                                        </Condition>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                <br/>
                
                <ErrorFields
                    errorFields={[
                        "freeRegistration",
                        "customRegistration",
                        "customPrice",
                        "priceNote",
                        "defaultUser",
                        "trialCoordinator",
                        "student",
                        "registrar",
                        "staff",
                        "attendeeFromJapanOrKorea",
                        "sponsor",
                        "freeSponsorRegistration",
                        "supervisorName",
                        "sponsorId",
                        "other",
                        "otherSpecification",
                        "registrationType"
                    ]}
                />
            </>
            )
        }
        //#endregion
    },
    {
        //#region package selection
        label: 'Package Selection',
        Inputs: () => {

        const {packageId} = usePackageId();

        const {packages} = useContext(PackageContext);

        const packageName = packages?.find(p => p.id === packageId)?.name;

        return (
            <>
                <Accordion elevation={4}>
                    <StyledAccordionSummary 
                        expandIcon={<FontAwesomeIcon fixedWidth icon={faChevronDown} color='white' />}
                    >
                    <Grid
                        container
                    >
                        <Grid 
                            item
                            xs={12}
                        >
                            <Typography variant="h3" color="white"><b>Package Selection</b></Typography>
                        </Grid>
                        {
                            packageName && (
                                <Grid 
                                    item
                                    xs={12}
                                >
                                    <Typography variant="h6" color='white' style={{paddingTop: "8px"}}><b>Package:</b> {packageName}</Typography>
                                </Grid>
                            )
                        }
                    </Grid>
                    </StyledAccordionSummary>
                    <AccordionDetails>
                        <Grid
                            container
                            spacing={2}
                        >
                                <Grid
                                    item
                                    xs={12}
                                    sx={{
                                        mt: 1
                                    }}
                                >
                                    <Packages />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
                
                <br/>

                <ErrorFields
                    errorFields={[
                        "packageId"
                    ]}
                />
            </>
        )
    }
        //#endregion
    },
    {  
        //#region Hotel and Travel
        label: 'Hotel and Travel',
        Inputs: () => {
        const {state: {value: hotel}, context: {setValue: setHotel}} = useScopedField<number>("hotelName", {value: true});
        
        useEffect(() => {
            if (hotel === undefined) {
                setHotel(1);
            }
        }, [hotel]);

        return (
            <>
                <Accordion elevation={4}>
                    <StyledAccordionSummary 
                        expandIcon={<FontAwesomeIcon fixedWidth icon={faChevronDown} color='white' />}
                    >
                        <Typography variant="h3" color="white"><b>Hotel and Travel</b></Typography>   
                    </StyledAccordionSummary>
                    <AccordionDetails>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Grid
                                container
                                spacing={4}
                                paddingTop={2}
                            >
                                <Grid
                                    item
                                    xs={12}
                                    //{...columnMapping}
                                >
                                    <Typography
                                        variant="h6"
                                        color='secondary'
                                    >
                                        Travel
                                    </Typography>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={8}
                                >
                                    <Typography>Did BCT arrange flights?</Typography>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                >
                                    <InputOnlyField
                                        propertyPath="bctFlights"
                                        component={ToggleButtonGroup}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                <Condition
                                    condition={bctFlightsArrangedCondition}
                                    subscription={valueSubscription}
                                    mode={ConditionMode.Show}
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <InputOnlyField
                                            label="Flight arrival"
                                            propertyPath="flightArrival"
                                            component={DateTimePicker}
                                            slotProps={{
                                                textField: {
                                                    variant: "standard"
                                                }
                                            }}

                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <InputOnlyField
                                            label="Flight departure"
                                            propertyPath="flightDeparture"
                                            component={DateTimePicker}
                                            slotProps={{
                                                textField: {
                                                    variant: "standard"
                                                }
                                            }}

                                        />
                                    </Grid>
                                </Condition>
                                <Grid
                                    item
                                    xs={12}
                                    md={8}
                                >
                                    <Typography>Did BCT arrange airport transfers?</Typography>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                >
                                    <InputOnlyField
                                        label="BCT Arranged Transfers"
                                        propertyPath="bctTransfers"
                                        component={ToggleButtonGroup}
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                <Condition
                                    condition={bctTransfersArrangedCondition}
                                    subscription={valueSubscription}
                                    mode={ConditionMode.Show}
                                >
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                >
                                    <InputOnlyField
                                        label="Airport pickup"
                                        propertyPath="airportTransfer"
                                        component={DateTimePicker}
                                        slotProps={{
                                            textField: {
                                                variant: "standard"
                                            }
                                        }}

                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                >
                                    <InputOnlyField
                                        label="Hotel pickup"
                                        propertyPath="hotelTransfer"
                                        component={DateTimePicker}
                                        slotProps={{
                                            textField: {
                                                variant: "standard"
                                            }
                                        }}

                                    />
                                </Grid>
                                <Grid
                                    item
                                    {...columnMapping}
                                >
                                    <InputOnlyField
                                        label="Cab charge"
                                        propertyPath="cabCharge"
                                        component={Text}
                                        variant="standard"
                                        margin="normal"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography color="text.secondary">$&nbsp;</Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    {...columnMapping}
                                >
                                    <InputOnlyField
                                        label="Number of cab charge"
                                        propertyPath="noCabCharge"
                                        component={Text}
                                        variant="standard"
                                        margin="normal"
                                        
                                    />
                                </Grid>
                                <Grid
                                    item
                                    {...columnMapping}
                                    sx={{marginTop: 3}}
                                >
                                    <InputOnlyField
                                        label="Cab Charge Sent"
                                        propertyPath="cabChargeSent"
                                        component={Checkbox}
                                    />
                                </Grid>
                                </Condition>
                                <Grid
                                    item
                                    xs={12}
                                    //{...columnMapping}
                                >
                                    <Typography
                                        variant="h6"
                                        color='secondary'
                                    >
                                        Hotel
                                    </Typography>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={3}
                                    //{...columnMapping}
                                >
                                    <InputOnlyField
                                        label="Affiliation"
                                        propertyPath="affiliation"
                                        component={Select}
                                        FormControlProps={{
                                            variant: "standard"
                                        }}
                                        sx={{marginTop:"normal"}}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={3}
                                    //{...columnMapping}
                                >
                                    <InputOnlyField
                                        label="Hotel Name"
                                        propertyPath="hotelName"
                                        component={Select}
                                        FormControlProps={{
                                            variant: "standard"
                                        }}
                                        sx={{marginTop:"normal"}}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={3}
                                >
                                    <InputOnlyField
                                        label="Accommodation Type"
                                        propertyPath="accomType"
                                        component={Select}
                                        FormControlProps={{
                                            variant: "standard"
                                        }}
                                />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={3}
                                    //{...columnMapping}
                                >
                                    <InputOnlyField
                                        label="Room Buddy"
                                        propertyPath="roomBuddy"
                                        component={Text}
                                        variant="standard"
                                        sx={{margin:"normal"}}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                >
                                    <InputOnlyField
                                        label="Hotel Checkin"
                                        propertyPath="hotelCheckin"
                                        component={DateTimePicker}
                                        slotProps={{
                                            textField: {
                                                variant: "standard"
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                >
                                    <InputOnlyField
                                        label="Hotel Checkout"
                                        propertyPath="hotelCheckout"
                                        component={DateTimePicker}
                                        slotProps={{
                                            textField: {
                                                variant: "standard"
                                            }
                                        }}

                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </AccordionDetails>
                </Accordion>
                
                <br/>
                
                <ErrorFields
                    errorFields={[
                        "affiliation",
                        "hotelName",
                        "accomType",
                        "roomBuddy",
                        "hotelCheckin",
                        "hotelCheckout",
                        "bctFlights",
                        "flightArrival",
                        "flightDeparture",
                        "bctTransfers",
                        "airportTransfer",
                        "hotelTransfer",
                        "cabCharge",
                        "noCabCharge",
                        "cabChargeSent"
                    ]}
                />
            </>
        )
        }
        //#endregion
    },
    {
        //#region Payment
        label: 'Payment',
        Inputs: () => {
            const [open, setOpen] = useState<{ index: number; isEdit: boolean; validationPass: boolean; }>();

            const { subscribe, getValue } = useFormActions();
        
            const [amountPaid, setAmountPaid] = useState(0);
            const [amountOwing, setAmountOwing] = useState(0);
        
            useEffect(() => {
                const getAmountPaid = (registration: Dtos.Registration) => registration.payments?.reduce((paid,p) => {
                    if (p.paymentOption == 8) {
                        return paid - (p.amountPaid ?? 0);
                    }
                    else{
                        return paid + (p.amountPaid ?? 0);
                    }
                }, 0) ?? 0;
        
                const getAmountOwing = (registration: Dtos.Registration) => registration.payments?.reduce((owing,p) => {
                    if (p.paymentOption == 8) {
                        return owing + (p.amountPaid ?? 0);
                    }
                    else{
                        return owing - (p.amountPaid ?? 0);
                    }
                }, registration.cost ?? 0) ?? registration.cost ?? 0;
        
                const value = getValue();
        
                setAmountOwing(getAmountOwing(value));
                setAmountPaid(getAmountPaid(value));
        
                const unsubscribe = subscribe((formState) => {
                    setAmountOwing(getAmountOwing(formState.value));
                    setAmountPaid(getAmountPaid(formState.value));
                }, {
                    value: true
                });
        
                return () => {
                    unsubscribe();
                }
            }, [setAmountPaid, setAmountOwing, subscribe, getValue ]);

            return (
                <>
                    <Accordion elevation={4}>
                        <StyledAccordionSummary 
                            expandIcon={<FontAwesomeIcon fixedWidth icon={faChevronDown} color='white' />}
                        >
                            <Grid
                                container
                            >
                                <Grid 
                                    item
                                    xs={12}
                                >
                                    <Typography variant="h3" color="white" style={{paddingBottom: "8px"}}><b>Payment</b></Typography>
                                </Grid>
                                <Grid 
                                    item
                                    xs={12}
                                    sm={12}
                                    md={6}
                                    lg={6}
                                    xl={3}
                                >
                                    <Typography variant="h6" color='white'><b>Amount Paid:</b> ${amountPaid}</Typography>
                                </Grid>
                                <Grid 
                                    item
                                    xs={12}
                                    sm={12}
                                    md={6}
                                    lg={6}
                                    xl={3}
                                >
                                    <Typography variant="h6" color='white'><b>Amount Owing:</b> ${amountOwing}</Typography>
                                </Grid>
                            </Grid>
                            
                        </StyledAccordionSummary>
                        <AccordionDetails>
                            <Grid
                                container
                                flexDirection="column"
                                sx={{
                                    mt: 2,
                                }}
                            >
                                <AdminPaymentDisplay/>
                                <Grid item xs={12}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mt: 5,
                                        }}
                                        color='secondary'
                                    >
                                        Payments
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <AdminPaymentTable
                                        setOpen={setOpen}
                                    />
                                    <Divider></Divider>
                                </Grid>
                                <AdminPaymentForm
                                    open={open}
                                    setOpen={setOpen}
                                    attemptNumber={0}
                                    amountOwing={amountOwing}
                                />
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                    
                    <br/>
                    
                    <ErrorFields
                        errorFields={[
                            "payments"
                        ]}
                    />
                </>
            )
        }
        //#endregion
    },
];

export default AdminRegistrationSteps;
